/**
 * Sub-aba "Cupom Desconto": cupom reutilizável, em percentual ou em valor fixo, com validade
 * opcional, ativável e desativável. Porte de `admin.html:2151-2180` e `7285-7436`; linhas 80 a 85
 * do inventário.
 *
 * O QUE FICA IGUAL MESMO PARECENDO DEFEITO: "hoje" é a data em UTC (`admin.html:7235` e `7420`), e o
 * cupom vale "Expirado" já no dia da validade (`admin.html:7341`), como no gift card.
 */

// Estado, memória, referência e efeito do React.
import { useCallback, useEffect, useRef, useState } from 'react';
// As peças da plataforma.
import { Botao, Campo, Selecao } from '@complexity/ui';
// As chamadas da área.
import { criarCupom, definirCupomAtivo, excluirCupom, listarCuponsEResgates } from '@studiolu/dados/areas/sistema';
// As linhas de `cupons_desconto` e dos resgates.
import type { LinhaCuponsDesconto, LinhaCuponsDescontoResgates } from '@studiolu/dados';
// Sessão e fonte de dados.
import { usePainel } from '../../contexto.js';
// O parágrafo do status, desenhado como o legado.
import { LinhaDeStatus } from '../../comum/status-temporario.js';
// A memória da área (campos e status que sobrevivem à ida a outra área) e o mínimo da validade.
import { MINIMO_DA_VALIDADE, useMemoria, useStatusDaArea } from './memoria.js';
// A moeda do legado.
import { parseMoeda } from '../../comum/moeda.js';
import { formatarMoeda } from '../../comum/formatos.js';
// O botão de copiar código do legado.
import { BotaoCopiarCodigo } from '../../comum/copiar-codigo.js';
// Hoje em UTC, a data de banco em DD/MM/AAAA, e a linha compacta.
import { LinhaCompacta, dataBR, hojeUTC } from '../financeiro/apoio.js';
// As peças visuais da área.
import { BotaoDaLinha, CartaoParam, LinhaParam, NotaSecao, TextoParam, type PropsDaSubAba } from './pecas.js';

// As duas opções do "Tipo", na ordem do legado (`admin.html:2162-2163`).
const OPCOES_DE_TIPO = [
  { valor: 'percentual', rotulo: 'Percentual (%)' },
  { valor: 'fixo', rotulo: 'Valor fixo (R$)' },
];

/**
 * O que a digitação vira no campo de valor (`admin.html:7304-7311`): no fixo, cada dígito é
 * centavo, com a moeda formatada; no percentual, só dígitos e vírgula ficam.
 */
function mascararValorDoCupom(tipo: string, digitado: string): string {
  if (tipo === 'fixo') {
    const digitos = digitado.replace(/\D/g, '');
    return digitos ? formatarMoeda(parseInt(digitos, 10) / 100) : '';
  }
  return digitado.replace(/[^\d,]/g, '');
}

/** A situação do cupom, com o tom (`statusCupom`, `admin.html:7339-7343`). */
function situacao(cupom: LinhaCuponsDesconto): { texto: string; ativo: boolean } {
  if (!cupom.ativo) return { texto: 'Desativado', ativo: false };
  if (cupom.validade && new Date(cupom.validade + 'T00:00:00') < new Date()) return { texto: 'Expirado', ativo: false };
  return { texto: 'Ativo', ativo: true };
}

// Os cupons e os resgates lidos juntos.
interface Listas {
  readonly cupons: readonly LinhaCuponsDesconto[];
  readonly resgates: readonly LinhaCuponsDescontoResgates[];
}

export function SubAbaCupons({ confirmar, alertar, recarga }: PropsDaSubAba) {
  const { dados } = usePainel();
  // `null` enquanto lê: o "Carregando…" do legado (`admin.html:7334`).
  const [listas, setListas] = useState<Listas | null>(null);
  // As últimas listas lidas (`cuponsCarregados` e `resgatesCuponsCarregados`).
  const carregadas = useRef<Listas>({ cupons: [], resgates: [] });
  // O formulário: o tipo começa em percentual, a primeira opção. Na memória da área, porque no
  // legado são campos fixos da página (`admin.html:2154-2177`) e a volta encontra o que ficou.
  const [codigo, setCodigo] = useMemoria('sistema:cupom:codigo', '');
  const [tipo, setTipo] = useMemoria('sistema:cupom:tipo', 'percentual');
  const [valor, setValor] = useMemoria('sistema:cupom:valor', '');
  const [validade, setValidade] = useMemoria('sistema:cupom:validade', '');
  // O parágrafo `#cupom-status`, fora da lista no legado (`admin.html:2178`).
  const [status, mostrarStatus] = useStatusDaArea('sistema:cupom:status');

  // Troca as listas e a referência juntas.
  const guardar = useCallback((novas: Listas) => {
    carregadas.current = novas;
    setListas(novas);
  }, []);

  // `carregarCupons` (`admin.html:7332-7337`): falha dos cupons mantém as listas anteriores.
  const carregar = useCallback(async () => {
    setListas(null);
    const resultado = await listarCuponsEResgates(dados);
    if (!resultado.ok) console.error('Erro ao carregar cupons:', resultado.erro.mensagem);
    guardar(resultado.ok ? resultado.dados : carregadas.current);
  }, [dados, guardar]);

  // Lê ao abrir a área e a cada clique na sub-aba.
  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  // "Desativar" / "Ativar" (`admin.html:7387-7396`).
  const alternar = useCallback(
    async (id: number) => {
      const cupom = carregadas.current.cupons.find((c) => c.id === id);
      if (!cupom) return;
      const resultado = await definirCupomAtivo(dados, id, !cupom.ativo);
      if (!resultado.ok) {
        mostrarStatus('Erro ao salvar: ' + resultado.erro.mensagem);
        return;
      }
      guardar({
        ...carregadas.current,
        cupons: carregadas.current.cupons.map((c) => (c.id === id ? { ...c, ativo: !c.ativo } : c)),
      });
    },
    [dados, guardar, mostrarStatus],
  );

  // "Excluir" (`admin.html:7398-7407`).
  const excluir = useCallback(
    async (id: number) => {
      if (!(await confirmar('Excluir esse cupom? Os resgates já feitos com ele também são apagados.'))) return;
      const resultado = await excluirCupom(dados, id);
      if (!resultado.ok) {
        await alertar('Erro ao excluir: ' + resultado.erro.mensagem);
        return;
      }
      guardar({ ...carregadas.current, cupons: carregadas.current.cupons.filter((c) => c.id !== id) });
    },
    [dados, confirmar, alertar, guardar],
  );

  // "Criar" (`admin.html:7410-7436`).
  const criar = useCallback(async () => {
    // O código aparado e em maiúsculas.
    const codigoLimpo = codigo.trim().toUpperCase();
    const dataDeValidade = validade || null;
    if (!codigoLimpo) {
      mostrarStatus('Digite um código.');
      return;
    }
    // Fixo pela moeda; percentual com a vírgula trocada por ponto.
    const numero = tipo === 'fixo' ? parseMoeda(valor) : parseFloat(valor.replace(',', '.'));
    if (!numero || numero <= 0) {
      mostrarStatus('Informe o valor do desconto.');
      return;
    }
    // 100: o teto de um desconto em percentual.
    if (tipo === 'percentual' && numero > 100) {
      mostrarStatus('Percentual não pode passar de 100.');
      return;
    }
    if (dataDeValidade && dataDeValidade < hojeUTC()) {
      mostrarStatus('A validade não pode ser anterior a hoje.');
      return;
    }
    mostrarStatus('Criando...');
    const resultado = await criarCupom(dados, {
      codigo: codigoLimpo,
      tipo_desconto: tipo,
      valor_desconto: numero,
      validade: dataDeValidade,
    });
    if (!resultado.ok) {
      // Código repetido tem frase própria; o resto vai com a mensagem crua (`admin.html:7427`).
      mostrarStatus(
        /duplicate key|unique/i.test(resultado.erro.mensagem)
          ? 'Já existe um cupom com esse código.'
          : 'Erro ao criar: ' + resultado.erro.mensagem,
      );
      return;
    }
    // O novo no topo; código, valor e validade voltam vazios, e o tipo fica.
    guardar({ ...carregadas.current, cupons: [resultado.dados, ...carregadas.current.cupons] });
    setCodigo('');
    setValor('');
    setValidade('');
    mostrarStatus('Cupom criado!');
  }, [codigo, tipo, valor, validade, dados, guardar, mostrarStatus, setCodigo, setValor, setValidade]);

  return (
    <div className="flex flex-col gap-cx-4">
      <div className="flex flex-col gap-cx-2">
        <NotaSecao>
          Cupom de desconto reutilizável (diferente do Gift Card, que é um voucher pessoal). A cliente informa o código no portal
          dela, na fase Diagnóstico Inicial, e o desconto é aplicado automaticamente quando você registrar o pagamento no
          Financeiro.
        </NotaSecao>
        <NotaSecao>
          <strong>Uma vez por pessoa:</strong> o mesmo e-mail não consegue usar o mesmo cupom duas vezes, mas o código pode ser
          usado por várias clientes diferentes até a validade vencer (ou até você desativar).
        </NotaSecao>
      </div>

      <LinhaCompacta>
        <Campo
          rotulo="Código"
          type="text"
          name="cupom-codigo"
          placeholder="Ex: AMIGA10"
          value={codigo}
          onChange={(evento) => setCodigo(evento.target.value)}
        />
        <Selecao
          rotulo="Tipo"
          opcoes={OPCOES_DE_TIPO}
          value={tipo}
          onChange={(evento) => {
            // REESCOLHER O MESMO TIPO NÃO LIMPA NADA, e é o legado: o gesto dele está no `change` do
            // `<select>` nativo (`admin.html:7297-7302`), e o `change` só dispara quando o valor MUDA
            // (medido no Chrome em 19/09/2026, divergência D2: com "10" digitado, reescolher
            // "Percentual (%)" pela letra ou pela lista não dispara nada e o valor fica). O `Selecao`
            // do Chassi chama `onChange` em toda escolha, inclusive na que já estava, então a
            // comparação que o `<select>` fazia sozinho é feita aqui.
            if (evento.target.value === tipo) return;
            // Mudou mesmo: troca rótulo e exemplo, e LIMPA o valor.
            setTipo(evento.target.value);
            setValor('');
          }}
        />
        <Campo
          rotulo={tipo === 'fixo' ? 'Valor (R$)' : 'Valor (%)'}
          type="text"
          placeholder={tipo === 'fixo' ? 'R$ 0,00' : '10'}
          value={valor}
          onChange={(evento) => setValor(mascararValorDoCupom(tipo, evento.target.value))}
        />
        {/* O mínimo do seletor é o dia da CARGA da página, em UTC, como o bloco do legado que roda
            uma vez só para os dois seletores (`admin.html:7234-7238`). */}
        <Campo
          rotulo="Validade (opcional)"
          type="date"
          min={MINIMO_DA_VALIDADE}
          value={validade}
          onChange={(evento) => setValidade(evento.target.value)}
        />
        <Botao onClick={() => void criar()}>Criar</Botao>
      </LinhaCompacta>
      <LinhaDeStatus texto={status} testid="status-cupom" />

      {listas === null && <TextoParam>Carregando…</TextoParam>}
      {listas !== null && listas.cupons.length === 0 && <TextoParam>Nenhum cupom criado ainda.</TextoParam>}
      {listas !== null && listas.cupons.length > 0 && (
        <CartaoParam testid="lista-cupons">
          {listas.cupons.map((cupom) => {
            const sit = situacao(cupom);
            // Percentual como número inteiro ou com decimais; fixo em reais (`admin.html:7356`).
            const valorTexto =
              cupom.tipo_desconto === 'percentual' ? `${Number(cupom.valor_desconto)}%` : formatarMoeda(Number(cupom.valor_desconto));
            const validadeTexto = cupom.validade ? ' · válido até ' + dataBR(cupom.validade) : ' · sem validade definida';
            const resgates = listas.resgates.filter((r) => r.cupom_id === cupom.id).length;
            return (
              <LinhaParam key={cupom.id} data-id={String(cupom.id)} className="md:items-start">
                <div className="min-w-0 flex-1 text-cx-sm">
                  <div className="flex flex-wrap items-center gap-x-cx-1">
                    <span className="font-mono text-cx-base font-semibold tracking-wide text-texto">{cupom.codigo}</span>
                    <BotaoCopiarCodigo codigo={cupom.codigo} />
                    <span>{`· ${valorTexto} de desconto`}</span>
                  </div>
                  <div className="mt-cx-1 text-texto-suave">{`${resgates} resgate${resgates === 1 ? '' : 's'}${validadeTexto}`}</div>
                  {/* Ativo em verde, Desativado e Expirado em vermelho (`admin.html:1231-1232`). */}
                  <div className={`mt-cx-1 font-semibold ${sit.ativo ? 'text-sucesso' : 'text-erro'}`} data-testid="situacao-cupom">
                    {sit.texto}
                  </div>
                </div>
                <span className="ml-auto flex shrink-0 flex-col items-end gap-cx-1">
                  <BotaoDaLinha onClick={() => void alternar(cupom.id)}>{cupom.ativo ? 'Desativar' : 'Ativar'}</BotaoDaLinha>
                  <BotaoDaLinha perigo onClick={() => void excluir(cupom.id)}>Excluir</BotaoDaLinha>
                </span>
              </LinhaParam>
            );
          })}
        </CartaoParam>
      )}
    </div>
  );
}
